import { Alert, Pressable, Text, View } from 'react-native';
import { CartaoApoio } from '../components/Recolhivel';
import type { Reg } from '../store/types';
import type { Colecao, ModDef, PainelProps } from './tipos';
import { dataBr, diasAte, junta, num, op } from './util';
import { avisoRotinas, criarRotinas, temRotinas, tirarRotinas } from './agenda';
import { mudarCampos } from '../store/modulos';
import { C } from '../components/theme';
import { addDays, today } from '../lib/date';
import { N_, t } from '../lib/i18n';

const proxima = (ultima: string | undefined, cada: number) => (cada > 0 ? addDays(ultima || today(), ultima ? cada : 0) : null);

/** Coloca rega e adubação no My Planner como lembretes repetidos. */
function agendar(r: Reg, calado = false) {
  const rot = [];
  if (num(r.c.rega) > 0) rot.push({ titulo: t('Regar: {p}', { p: r.c.nome }), emoji: '💧', hora: r.c.hora || null, cada: num(r.c.rega), inicio: proxima(r.c.ultimaRega, num(r.c.rega))! });
  if (num(r.c.adubo) > 0) rot.push({ titulo: t('Adubar: {p}', { p: r.c.nome }), emoji: '🌱', hora: r.c.hora || null, cada: num(r.c.adubo), inicio: proxima(r.c.ultimoAdubo, num(r.c.adubo))! });
  if (!rot.length) return calado ? undefined : Alert.alert(t('Informe de quantos em quantos dias regar ou adubar.'));
  const n = criarRotinas('plantas', r, rot);
  if (!calado) Alert.alert(t('Calendário da planta'), avisoRotinas(n));
}

function RegarHoje({ ctx, lista }: PainelProps) {
  const hoje = today();
  const sede = lista.filter(r => num(r.c.rega) > 0 && (!r.c.ultimaRega || addDays(r.c.ultimaRega, num(r.c.rega)) <= hoje));
  if (!sede.length) return lista.length ? <Text style={{ color: C.ok, fontSize: 13 }}>💧 {t('Todas as plantas estão regadas.')}</Text> : null;
  return (
    <CartaoApoio titulo={'💧 ' + t('Regar hoje')} chave={'plantas:regar'} resumo={String(sede.length)} gap={8}>
      {sede.map(r => (
        <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ color: C.text, flex: 1 }}>{r.c.nome}{r.c.local ? <Text style={{ color: C.sub }}> · {r.c.local}</Text> : null}</Text>
          <Pressable onPress={() => mudarCampos(ctx.mod, r.id, { ultimaRega: hoje })} style={{ backgroundColor: ctx.cor, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6 }}>
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>{t('Reguei')}</Text>
          </Pressable>
        </View>
      ))}
    </CartaoApoio>
  );
}

const planta: Colecao = {
  id: 'planta', nome: N_('Plantas'), um: N_('Planta'), icone: '🪴',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto', ph: N_('Ex.: Costela-de-adão da sala') },
    { k: 'especie', r: N_('Espécie'), t: 'texto' },
    { k: 'local', r: N_('Onde fica'), t: 'texto', ph: N_('Ex.: varanda, sala') },
    { k: 'luz', r: N_('Luz'), t: 'opcao', op: [N_('Sol pleno'), N_('Meia-sombra'), N_('Luz indireta'), N_('Sombra')] },
    { k: 'solo', r: N_('Solo ou substrato'), t: 'opcao', op: [N_('Universal'), N_('Cactos e suculentas'), N_('Orquídeas'), N_('Rico em matéria orgânica'), N_('Arenoso'), N_('Ácido'), N_('Outro')] },
    { k: 'rega', r: N_('Regar a cada quantos dias'), t: 'numero' },
    { k: 'ultimaRega', r: N_('Última rega'), t: 'data' },
    { k: 'adubo', r: N_('Adubar a cada quantos dias'), t: 'numero' },
    { k: 'ultimoAdubo', r: N_('Última adubação'), t: 'data' },
    { k: 'hora', r: N_('Horário do lembrete'), t: 'hora' },
    { k: 'notas', r: N_('Cuidados'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '',
  sub: r => {
    const pr = num(r.c.rega) > 0 && r.c.ultimaRega ? addDays(r.c.ultimaRega, num(r.c.rega)) : null;
    return junta(op(r.c.luz), num(r.c.rega) && t('rega a cada {n} dias', { n: num(r.c.rega) }), pr && (diasAte(pr) <= 0 ? t('regar hoje') : t('próxima rega {d}', { d: dataBr(pr) })), temRotinas(r) && '🗓️');
  },
  ordem: 'titulo', anexos: true, painel: RegarHoje,
  aoSalvar: r => { if (temRotinas(r)) agendar(r, true); },
  acoes: (r, ctx) => [
    { label: '💧 ' + t('Reguei hoje'), onPress: () => mudarCampos(ctx.mod, r.id, { ultimaRega: today() }) },
    { label: '🌱 ' + t('Adubei hoje'), onPress: () => mudarCampos(ctx.mod, r.id, { ultimoAdubo: today() }) },
    { label: '🗓️ ' + (temRotinas(r) ? t('Atualizar rega e adubo na agenda') : t('Colocar rega e adubo na agenda')), onPress: () => agendar(r) },
    ...(temRotinas(r) ? [{ label: t('Tirar da agenda'), onPress: () => tirarRotinas('plantas', r) }] : []),
  ],
  vazio: N_('Cadastre cada planta com luz, solo e de quantos em quantos dias regar e adubar. O app monta o calendário na agenda.'),
};

const replantio: Colecao = {
  id: 'replantio', pai: 'planta', nome: N_('Replantios'), um: N_('Replantio'), icone: '🪏',
  campos: [
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'vaso', r: N_('Vaso'), t: 'texto', ph: N_('Ex.: cerâmica 30 cm') },
    { k: 'substrato', r: N_('Substrato usado'), t: 'texto' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => dataBr(r.c.data) || t('Replantio'), sub: r => junta(r.c.vaso, r.c.substrato), ordem: 'data-desc', anexos: true,
  novo: () => ({ data: today() }),
};

const diarioPlanta: Colecao = {
  id: 'diarioPlanta', pai: 'planta', nome: N_('Fotos e notas'), um: N_('Registro'), icone: '📷',
  campos: [{ k: 'data', r: N_('Data'), t: 'data' }, { k: 'texto', r: N_('Como ela está'), t: 'longo' }],
  titulo: r => dataBr(r.c.data), sub: r => String(r.c.texto || '').slice(0, 100), ordem: 'data-desc', anexos: true,
  novo: () => ({ data: today() }),
};

export const PLANTAS: ModDef = { id: 'plantas', colecoes: [planta, replantio, diarioPlanta] };
